import {
    AccessTimeOutlined,
    AccountBalanceOutlined,
    ArticleOutlined,
    AssignmentIndOutlined,
    BadgeOutlined,
    Balance,
    BusinessOutlined,
    CampaignOutlined,
    ChatBubbleOutline,
    DescriptionOutlined,
    GavelOutlined,
    GroupWorkOutlined,
    GroupsOutlined,
    HandshakeOutlined,
    InboxOutlined,
    PaymentsOutlined,
    PersonOutline,
    ReceiptLongOutlined,
    SavingsOutlined,
    SellOutlined,
    SvgIconComponent,
    UserOutlined,
    WorkOutlineOutlined,
    WorkspacePremiumOutlined,
} from 'components/icons';
import { GlobalSearchEntityType, GlobalSearchSecondaryLineKind } from 'types/globalSearch';
import { ENTITY_TYPE_FAMILY, FAMILY } from 'utils/families';

/**
 * How each record family is drawn wherever search lists it — the dropdown, the sheet and the results
 * page all read these two maps, so a family looks the same on every surface.
 *
 * Outside the ⌘K palette the glyph is the row's only type cue: the rows carry no type chip, and the
 * family's name appears once, on the section heading above them. The palette names Case, Contact,
 * Invoice and BD effort in a type column instead, and keeps the glyph for the other families.
 */
export const GLOBAL_SEARCH_ENTITY_ICON: Record<GlobalSearchEntityType, SvgIconComponent> = {
    MATTER: Balance,
    INVOICE: ReceiptLongOutlined,
    PARTY: PersonOutline,
    PREPAYMENT: SavingsOutlined,
    TIMECARD: AccessTimeOutlined,
    EFFORT: CampaignOutlined,
    EXPENSE: PaymentsOutlined,
    CORRESPONDENCE: InboxOutlined,
    BANK_TRANSACTION: AccountBalanceOutlined,
    CONTRACT: DescriptionOutlined,
    PRICELIST: SellOutlined,
    PROJECT: WorkOutlineOutlined,
    EXPERIENCE_RECORD: WorkspacePremiumOutlined,
    DEBRIEFING: ChatBubbleOutline,
    PARTY_GROUP: GroupWorkOutlined,
    EMPLOYEE: UserOutlined,
    TEAM: GroupsOutlined,
    OFFICE: BusinessOutlined,
    PRACTICE_AREA: GavelOutlined,
    JOB_POSITION: BadgeOutlined,
    JOB_ROLE: AssignmentIndOutlined,
    DELEGATION: HandshakeOutlined,
};

/**
 * The families named in the singular and in their own ink where a row or tile says what it is: the ⌘K palette's
 * type column and Home's Continue tiles. Most other families' names do not fit there, so the palette keeps their
 * glyph and a tile their plural label.
 */
export const ENTITY_TYPE_LABEL: Partial<Record<GlobalSearchEntityType, { labelId: string; className: string }>> = {
    MATTER: { labelId: 'matters.matter', className: FAMILY[ENTITY_TYPE_FAMILY.MATTER].ink },
    PARTY: { labelId: 'contacts.contact', className: FAMILY[ENTITY_TYPE_FAMILY.PARTY].ink },
    INVOICE: { labelId: 'invoices.invoice', className: FAMILY[ENTITY_TYPE_FAMILY.INVOICE].ink },
    EFFORT: { labelId: 'expenses.bd-effort', className: FAMILY[ENTITY_TYPE_FAMILY.EFFORT].ink },
};

/**
 * The plural name of each family, taken from the copy its own list page already uses rather than
 * translated a second time here — a section heading that reads differently from the menu entry it
 * sends the user to is the failure this avoids.
 */
export const GLOBAL_SEARCH_ENTITY_LABEL_ID: Record<GlobalSearchEntityType, string> = {
    MATTER: 'matters.matters',
    INVOICE: 'menu.invoices',
    PARTY: 'contacts.contacts',
    PREPAYMENT: 'menu.prepayments',
    TIMECARD: 'timecards.timecards',
    EFFORT: 'menu.efforts',
    EXPENSE: 'menu.expenses',
    CORRESPONDENCE: 'menu.correspondence',
    BANK_TRANSACTION: 'menu.bank-transfers',
    CONTRACT: 'contracts.contracts',
    PRICELIST: 'pricelists.pricelists',
    PROJECT: 'projects.projects',
    EXPERIENCE_RECORD: 'experience-records.experience-records',
    DEBRIEFING: 'debriefing.debriefings',
    PARTY_GROUP: 'menu.groups',
    EMPLOYEE: 'menu.colleagues',
    TEAM: 'teams.teams',
    OFFICE: 'offices.offices',
    PRACTICE_AREA: 'menu.practice-areas',
    JOB_POSITION: 'menu.job-positions',
    JOB_ROLE: 'menu.job-roles',
    DELEGATION: 'menu.delegations',
};

/**
 * Where each family's section sits, whatever its count: contacts, groups, cases and colleagues first, then
 * the rest in the order of the left menu, and the back-office families last.
 */
export const GLOBAL_SEARCH_SECTION_ORDER: Record<GlobalSearchEntityType, number> = {
    PARTY: 0,
    PARTY_GROUP: 1,
    MATTER: 2,
    EMPLOYEE: 3,
    CONTRACT: 4,
    PROJECT: 5,
    DELEGATION: 6,
    TIMECARD: 7,
    EFFORT: 8,
    EXPERIENCE_RECORD: 9,
    CORRESPONDENCE: 10,
    DEBRIEFING: 11,
    INVOICE: 12,
    PREPAYMENT: 13,
    EXPENSE: 14,
    PRICELIST: 15,
    BANK_TRANSACTION: 16,
    TEAM: 17,
    OFFICE: 18,
    PRACTICE_AREA: 19,
    JOB_POSITION: 20,
    JOB_ROLE: 21,
};

/** Pages and actions are no family — they have no record anywhere — so they carry a glyph of their own. */
export const GLOBAL_SEARCH_PAGE_ICON = ArticleOutlined;

/**
 * The second line's colour, which is the only thing that says which of the two kinds it is: a quote
 * of the matched text, or the record's own facts. Both surfaces read it, so a row does not change
 * meaning between the dropdown and the results page.
 */
export function secondaryLineClass(kind: GlobalSearchSecondaryLineKind | null): string {
    return kind === 'FACTS' ? 'text-grey-500' : 'text-grey-600';
}
